<template>
  <div>
    <b-card class="mb-4">
      <TitleTable titleTable="Admin - Agent Relations" iconTable="fas fa-user-tie" />

      <div class="m-1">
        <b-row>
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <treeselect
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="w-100"
            />
          </b-col>
          <b-col cols="12" md="4" class="mb-1 mb-md-0">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by admin or agent"
            />
          </b-col>
          <b-col cols="12" md="4" class="d-flex justify-content-end">
            <GoBack class="mr-2" />
            <b-button variant="primary" @click="openCreateModal" :disabled="loading">
              <span v-if="!loading" class="text-nowrap">Add relation</span>
              <span v-else>
                <SpinnerLoading />
              </span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="filteredRows"
        :fields="fields"
        head-variant="light"
        hover
        bordered
        responsive
        primary-key="relationId"
        class="position-relative min-height-table"
        show-empty
        empty-text="No matching records found"
      >
        <template #cell(admin)="data">
          <div class="d-flex flex-column">
            <span class="font-weight-semibold">{{ data.item.adminName }}</span>
            <small style="color: #495057 !important;">{{ data.item.adminEmail || "No email" }}</small>
          </div>
        </template>
        <template #cell(agent)="data">
          <div class="d-flex flex-column">
            <span class="font-weight-semibold">{{ data.item.agentName }}</span>
            <small style="color: #495057 !important;">{{ data.item.agentEmail || "No email" }}</small>
          </div>
        </template>
        <template #cell(actions)="data">
          <!-- <b-button
            v-b-tooltip.hover.top="data.item.relationId ? 'Edit' : 'No relation ID available'"
            variant="outline-primary"
            class="btn-icon btn-circle border-0 mr-1"
            :disabled="!data.item.relationId"
            @click="openEditModal(data.item)"
          >
            <i class="fas fa-pen icon-nm"></i>
          </b-button> -->
          <b-button
            v-b-tooltip.hover.top="data.item.relationId ? 'Delete' : 'No relation ID available'"
            variant="outline-danger"
            class="btn-icon btn-circle border-0"
            :disabled="!data.item.relationId"
            @click="confirmDelete(data.item)"
          >
            <i class="fas fa-trash-alt icon-nm"></i>
          </b-button>
        </template>
      </b-table>

      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalRecords"
        :perPage="perPage"
        :currentPage="currentPage"
        @page-changed="pageChanged"
      />
    </b-card>

    <ToastNotification ref="toast" />

    <b-modal
      id="admin-agent-user-modal"
      :title="modalTitle"
      :visible.sync="modalVisible"
      size="lg"
      @hidden="resetForm"
      hide-footer
      centered
    >
      <b-form @submit.prevent="submitForm">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Admin" label-for="admin-select">
              <treeselect
                id="admin-select"
                v-model="form.admin_user_id"
                :options="adminSelectOptions"
                :disabled="saving"
                :clearable="false"
                :searchable="true"
                placeholder="Buscar admin..."
                class="w-100"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Agent" label-for="agent-select">
              <treeselect
                id="agent-select"
                v-model="form.agent_user_id"
                :options="agentSelectOptions"
                :disabled="saving"
                :clearable="false"
                :searchable="true"
                placeholder="Buscar agent..."
                class="w-100"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" class="d-flex justify-content-end">
            <b-button variant="secondary" class="mr-2" @click="modalVisible = false" :disabled="saving">
              Cancel
            </b-button>
            <b-button variant="primary" type="submit" :disabled="saving">
              <span v-if="!saving">{{ modalSubmitLabel }}</span>
              <span v-else>
                <b-spinner small class="mr-1" label="Saving"></b-spinner>
                Saving...
              </span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from "bootstrap-vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import {
  listAdminAgentUsers,
  createAdminAgentUser,
  updateAdminAgentUser,
  deleteAdminAgentUser,
  listAgentsCatalog,
  listAdminsCatalog,
} from "@/core/services/api/admin/adminAgentUser";
import ToastNotification from "@/components/ToastNotification";
import PaginationTable from "@/components/PaginationTable";
import TitleTable from "@/components/TitleTable.vue";
import SpinnerLoading from "@/components/SpinnerLoading.vue";
import GoBack from "@/components/ButtonBack.vue";
import Swal from "sweetalert2";

const composeName = (user = {}) => {
  const parts = [user.name, user.lastname].filter(Boolean);
  return parts.length ? parts.join(" ") : user.email || `User ${user.id}`;
};

export default {
  name: "TableAdminAgentUser",
  components: {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BRow,
    BSpinner,
    BTable,
    Treeselect,
    ToastNotification,
    PaginationTable,
    TitleTable,
    SpinnerLoading,
    GoBack,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      fields: [
        { key: "relationId", label: "ID", sortable: true },
        { key: "admin", label: "Admin" },
        { key: "agent", label: "Agent" },
        { key: "created_at", label: "Created at", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      rows: [],
      rawAdmins: [],
      loading: false,
      saving: false,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100].map((value) => ({
        id: value,
        label: `Show ${value} entries`,
      })),
      currentPage: {
        page: 1,
      },
      dataMeta: { from: 1, to: 1, of: 1 },
      totalRecords: 0,
      searchQuery: "",
      modalVisible: false,
      modalMode: "create",
      form: {
        admin_user_id: null,
        agent_user_id: null,
      },
      relationId: null,
      agentsCatalog: [],
      adminCatalog: [],
    };
  },
  computed: {
    filteredRows() {
      if (!this.searchQuery) return this.rows;
      const query = this.searchQuery.toLowerCase();
      return this.rows.filter((item) =>
        [item.adminName, item.adminEmail, item.agentName, item.agentEmail]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query))
      );
    },
    adminSelectOptions() {
      return this.adminCatalog.map((admin) => ({
        id: admin.id,
        label: composeName(admin),
      }));
    },
    agentSelectOptions() {
      return this.agentsCatalog.map((agent) => ({
        id: agent.id,
        label: composeName(agent),
      }));
    },
    modalTitle() {
      return this.modalMode === "create"
        ? "Add admin-agent relation"
        : "Edit admin-agent relation";
    },
    modalSubmitLabel() {
      return this.modalMode === "create" ? "Create" : "Update";
    },
  },
  watch: {
    perPage() {
      this.fetchRelations(1);
    },
  },
  mounted() {
    this.fetchAgentsCatalog();
    this.fetchAdminCatalog();
    this.fetchRelations();
  },
  methods: {
    /**
     * Extrae el relationId de un objeto agent con su pivot
     * El ID de la relación está en pivot.id según la estructura del API
     */
    extractRelationId(agent) {
      if (!agent) return null;
      
      const pivot = agent?.pivot || {};
      
      // El ID de la relación está directamente en pivot.id
      if (pivot.id !== null && pivot.id !== undefined) {
        const numValue = Number(pivot.id);
        if (!isNaN(numValue) && numValue > 0) {
          return numValue;
        }
      }
      
      return null;
    },
    /**
     * Formatea una fecha para mostrar solo la fecha sin la hora
     * @param {String} dateString - Fecha en formato string
     * @returns {String} - Fecha formateada (YYYY-MM-DD) o "-" si no hay fecha
     */
    formatDateOnly(dateString) {
      if (!dateString || dateString === "-") return "-";
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        return "-";
      }
    },
    async fetchRelations(page = 1) {
      this.loading = true;
      try {
        const params = { page, per_page: this.perPage };
        const response = await listAdminAgentUsers(params);
        
        // Manejar diferentes estructuras de respuesta
        let payload = {};
        let items = [];
        
        if (response?.data) {
          payload = response.data;
          items = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];
        } else if (Array.isArray(response)) {
          items = response;
        }

        this.rawAdmins = items.map((admin) => ({
          id: admin.id,
          name: admin.name,
          lastname: admin.lastname,
          email: admin.email,
          agents: Array.isArray(admin.agents) ? admin.agents : [],
        }));

        const rows = [];
        this.rawAdmins.forEach((admin) => {
          const agents = admin.agents;
          if (!agents.length) {
            rows.push({
              relationId: null,
              adminId: admin.id,
              adminName: composeName(admin),
              adminEmail: admin.email,
              agentId: null,
              agentName: "Not assigned",
              agentEmail: "",
              created_at: "-",
            });
            return;
          }

          agents.forEach((agent) => {
            const pivot = agent?.pivot || {};
            // El relationId es directamente pivot.id
            const relationId = pivot.id ? Number(pivot.id) : null;
            
            rows.push({
              relationId,
              adminId: admin.id,
              adminName: composeName(admin),
              adminEmail: admin.email,
              agentId: agent.id,
              agentName: composeName(agent),
              agentEmail: agent.email,
              created_at: this.formatDateOnly(pivot.created_at || agent.created_at || "-"),
            });
          });
        });

        this.rows = rows;
        this.totalRecords = payload.total || payload.totalRecords || rows.length;
        this.dataMeta = {
          from: payload.from || (rows.length ? 1 : 0),
          to: payload.to || rows.length,
          of: payload.total || payload.totalRecords || rows.length,
        };
        this.currentPage.page = payload.current_page || payload.currentPage || page;
      } catch (error) {
        this.rows = [];
        this.$refs.toast.danger(
          error?.response?.data?.message || "Could not load admin-agent relations."
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchAgentsCatalog() {
      try {
        const response = await listAgentsCatalog();
        const data = Array.isArray(response?.data) ? response.data : response;
        this.agentsCatalog = Array.isArray(data) ? data : [];
      } catch (error) {
        this.agentsCatalog = [];
      }
    },
    async fetchAdminCatalog() {
      try {
        const response = await listAdminsCatalog();
        const data = Array.isArray(response?.data) ? response.data : response;
        this.adminCatalog = Array.isArray(data) ? data : [];
      } catch (error) {
        this.adminCatalog = [];
      }
    },
    pageChanged(page) {
      this.fetchRelations(page);
    },
    openCreateModal() {
      this.modalMode = "create";
      this.form = { admin_user_id: null, agent_user_id: null };
      this.relationId = null;
      this.modalVisible = true;
    },
    openEditModal(row) {
      const relationId = row.relationId || row.relation_id || null;
      if (!relationId) {
        this.$refs.toast.danger("No se puede editar: ID de relación no disponible.");
        return;
      }
      
      if (!row.adminId || !row.agentId) {
        this.$refs.toast.danger("No se puede editar: información incompleta.");
        return;
      }
      
      this.modalMode = "edit";
      this.relationId = Number(relationId);
      this.form = {
        admin_user_id: Number(row.adminId),
        agent_user_id: Number(row.agentId),
      };
      this.modalVisible = true;
    },
    resetForm() {
      this.form = { admin_user_id: null, agent_user_id: null };
      this.relationId = null;
      this.modalMode = "create";
      this.saving = false;
    },
    async submitForm() {
      if (!this.form.admin_user_id || !this.form.agent_user_id) {
        this.$refs.toast.danger("Por favor, completa todos los campos requeridos.");
        return;
      }

      this.saving = true;
      
      try {
        if (this.modalMode === "create") {
          const payload = {
            admin_user_id: Number(this.form.admin_user_id),
            agent_user_id: Number(this.form.agent_user_id),
          };
          await createAdminAgentUser(payload);
          this.$refs.toast.success("Relación creada exitosamente.");
        } else {
          if (!this.relationId) {
            this.$refs.toast.danger("No se puede actualizar: ID de relación no disponible.");
            this.saving = false;
            return;
          }
          
          // Asegurar que el ID de la relación esté incluido en el payload
          const payload = {
            id: Number(this.relationId),
            relation_id: Number(this.relationId),
            admin_user_id: Number(this.form.admin_user_id),
            agent_user_id: Number(this.form.agent_user_id),
          };
          
          await updateAdminAgentUser(this.relationId, payload);
          this.$refs.toast.success("Relación actualizada exitosamente.");
        }
        this.modalVisible = false;
        this.fetchRelations(this.currentPage.page);
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error ||
                            error?.message ||
                            "Operación fallida. Por favor, intenta nuevamente.";
        this.$refs.toast.danger(errorMessage);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(row) {
      const relationId = row.relationId || row.relation_id || null;
      if (!relationId) {
        this.$refs.toast.danger("No se puede eliminar: ID de relación no disponible.");
        return;
      }
      Swal.fire({
        title: "Eliminar relación",
        text: "Esta acción no se puede deshacer. ¿Deseas continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-secondary ml-2",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.deleteRelation(Number(relationId));
        }
      });
    },
    async deleteRelation(relationId) {
      try {
        await deleteAdminAgentUser(relationId);
        this.$refs.toast.success("Relation deleted successfully.");
        this.fetchRelations(this.currentPage.page);
      } catch (error) {
        this.$refs.toast.danger(
          error?.response?.data?.message || "Delete operation failed."
        );
      }
    },
  },
};
</script>

<style scoped>
.btn-icon {
  width: 36px;
  height: 36px;
}
</style>

